import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  SharedLoading,
  SharedFeatureError,
  SharedCollapsibleIntro,
} from "../../../shared";
import { selectUser } from "../../../../store/slices/authentication";
import {
  useGetBulletinPostsQuery,
  useGetAdminStatusQuery,
} from "../../../../store/slices/api";
import PostCard from "./PostCard";
import {
  CATEGORIES,
  CATEGORY_COLORS,
  categoryLabel,
  normalizeCategory,
} from "../categories";

dayjs.extend(utc);
dayjs.extend(timezone);

const FILTER_OPTIONS = ["All", ...CATEGORIES];

const ANY_TIME = "Any time";
const DATE_FILTERS = [ANY_TIME, "This month", "Last 3 months", "This year"];

/** True if the post falls inside the chosen window. Compared in the viewer's local time. */
function matchesDateFilter(post, window) {
  if (window === ANY_TIME) return true;
  const posted = dayjs(post.created_at).utc().local();
  const now = dayjs();
  switch (window) {
    case "This month":
      return posted.isSame(now, "month");
    case "Last 3 months":
      return posted.isAfter(now.subtract(3, "month"));
    case "This year":
      return posted.isSame(now, "year");
    default:
      return true;
  }
}

const monthOf = (post) =>
  dayjs(post.created_at).utc().local().format("MMMM YYYY");

/**
 * Split an already-sorted (newest-first) list of posts into consecutive runs
 * that share a month, so we can print one subtle heading per run.
 */
function groupByMonth(posts) {
  const runs = [];
  posts.forEach((post) => {
    const month = monthOf(post);
    const current = runs[runs.length - 1];
    if (current && current.month === month) current.posts.push(post);
    else runs.push({ month, posts: [post] });
  });
  return runs;
}

/**
 * Sticks to the top of the viewport while you scroll through that month's posts,
 * then gets pushed off by the next month's heading. Only rendered when a section
 * actually spans more than one month — otherwise it marks no separation and is
 * just noise (each card already shows its own date).
 */
function MonthDivider({ month }) {
  return (
    <div className="sticky top-0 z-10 -mx-[4px] px-[4px] pt-[10px] pb-[8px] backdrop-blur bg-dupontCream/95 dark:bg-darkPrimary/95">
      <div className="flex flex-row items-center gap-x-[12px]">
        <span className="text-[13px] font-display font-bold uppercase tracking-[0.1em] text-dupontBlue dark:text-dupontGold whitespace-nowrap">
          {month}
        </span>
        <div className="h-[1px] flex-1 bg-dupontGold/40" />
      </div>
    </div>
  );
}

function PostGrid({ posts, isAdmin }) {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-[16px]">
      {posts.map((post) => (
        <PostCard key={post.id} post={post} isAdmin={isAdmin} />
      ))}
    </div>
  );
}

/** One expand/collapse section for a single category. */
function CategorySection({ category, posts, isAdmin, open, onToggle }) {
  const colors = CATEGORY_COLORS[category] || CATEGORY_COLORS.other;
  const panelId = `bulletin-section-${category.replace(/\s+/g, "-")}`;
  const monthRuns = useMemo(() => groupByMonth(posts), [posts]);

  return (
    <section className="flex flex-col gap-y-[12px]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex flex-row items-center gap-x-[12px] w-full text-left group"
      >
        <span
          className="text-[11px] transition-transform duration-200 text-dupontGray dark:text-darkTextSecondary"
          style={{
            display: "inline-block",
            transform: open ? "rotate(90deg)" : "rotate(0deg)",
          }}
          aria-hidden="true"
        >
          &#9654;
        </span>
        <span
          className="text-[13px] font-bold px-[12px] py-[4px] rounded-full whitespace-nowrap"
          style={{ backgroundColor: colors.bg, color: colors.color }}
        >
          {categoryLabel(category)}
        </span>
        <span className="text-[13px] text-dupontGray dark:text-darkTextSecondary font-medium whitespace-nowrap">
          {posts.length} post{posts.length !== 1 ? "s" : ""}
        </span>
        <span className="h-[1px] flex-1 bg-dupontGold/30 group-hover:bg-dupontGold/60 transition-colors" />
      </button>

      {open && (
        <div id={panelId} className="flex flex-col gap-y-[12px] pl-[23px]">
          {monthRuns.length > 1 ? (
            monthRuns.map((run) => (
              <React.Fragment key={run.month}>
                <MonthDivider month={run.month} />
                <PostGrid posts={run.posts} isAdmin={isAdmin} />
              </React.Fragment>
            ))
          ) : (
            // Every post here is from the same month — a heading would separate nothing.
            <PostGrid posts={posts} isAdmin={isAdmin} />
          )}
        </div>
      )}
    </section>
  );
}

function BulletinPosts() {
  const user = useSelector(selectUser);
  const individualId = user?.externalId ? parseInt(user.externalId) : null;
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [dateFilter, setDateFilter] = useState(ANY_TIME);
  const [search, setSearch] = useState("");
  // Categories are expanded unless explicitly collapsed, so arriving at the
  // Bulletin still shows real posts rather than a wall of closed headers.
  const [collapsed, setCollapsed] = useState({});

  const { data: bulletinPosts, isLoading, isError, refetch } =
    useGetBulletinPostsQuery();

  const { data: adminData } = useGetAdminStatusQuery(individualId, {
    skip: !individualId,
  });

  const isAdmin = adminData?.is_admin || false;

  const posts = useMemo(() => bulletinPosts || [], [bulletinPosts]);

  // The backend already sorts pinned first, then newest first. Filtering and
  // partitioning below preserve that order, so we never re-sort.
  const { pinned, sections, matchCount } = useMemo(() => {
    const query = search.trim().toLowerCase();
    const matchesSearch = (post) =>
      !query ||
      (post.title || "").toLowerCase().includes(query) ||
      (post.content || "").toLowerCase().includes(query);

    const visible = posts.filter(
      (post) =>
        matchesSearch(post) &&
        matchesDateFilter(post, dateFilter) &&
        (categoryFilter === "All" ||
          normalizeCategory(post.category) === categoryFilter)
    );

    const pinnedPosts = visible.filter((post) => post.is_pinned);
    const rest = visible.filter((post) => !post.is_pinned);

    const grouped = CATEGORIES.map((category) => ({
      category,
      posts: rest.filter((post) => normalizeCategory(post.category) === category),
    })).filter((group) => group.posts.length > 0);

    return { pinned: pinnedPosts, sections: grouped, matchCount: visible.length };
  }, [posts, search, categoryFilter, dateFilter]);

  // Gate on isLoading (first load) so background refreshes don't blank the list,
  // and show a friendly retry instead of an endless spinner if it fails.
  if (isLoading) {
    return <SharedLoading text="Loading posts..." />;
  }
  if (isError || !bulletinPosts) {
    return <SharedFeatureError onRetry={refetch} />;
  }

  const isSearching = search.trim().length > 0;
  const isFiltering =
    isSearching || categoryFilter !== "All" || dateFilter !== ANY_TIME;

  return (
    <div className="flex flex-col gap-y-[24px]">
      {/* Header area — the former "Landing" tab's copy, now collapsed above the live posts */}
      <SharedCollapsibleIntro
        heading="See What Your Cousins Are Sharing"
        teaser={
          <>
            Organizing, celebrating, and connecting. Have something to post? Head
            over to the{" "}
            <span className="font-bold text-dupontBlue dark:text-darkText">
              Create Post
            </span>{" "}
            tab.
          </>
        }
      >
        <p className="text-[17px] font-body text-dupontBlue dark:text-darkText leading-relaxed">
          This family needs a megaphone. We can all ignore if we'd like, but
          something that everyone can hear and allows for a centralization of
          information. This tab will be an announcement, bulletin, and,
          depending on user activity, an explore page all in one. And right
          next door, the Blog holds the longer reads: family stories, history,
          and the places that made us.
        </p>
        <p className="text-[17px] font-body text-dupontBlue dark:text-darkText leading-relaxed">
          Hopefully acting as a hub of both wonderfully informative and
          completely inane cousin-oriented information.
        </p>
        <p className="text-[17px] font-body text-dupontBlue dark:text-darkText leading-relaxed">
          It could be a local organization that you are working with that may
          need volunteers or some initiative you are passionate about and
          looking to connect over a mutual interest. Could be an event in a
          given cousin-populated area that you may have tickets for or want to
          get some eyes on. The possibilities are endless.
        </p>
        <p className="text-[17px] font-body text-dupontBlue dark:text-darkText leading-relaxed">
          Moreover, as the app develops more features fit for our proprietary
          needs, the bulletin will be there to announce the new functions. We
          will have an explanation and a graphic to detail how the addition
          could be used. This pseudo-announcement section can be used to update
          about bug fixes all the way to sending a push notification reminding
          people to buy tickets before deadlines. Allowing a semi-passive way to
          build easy connections.
        </p>
      </SharedCollapsibleIntro>

      {/* Search + category filter */}
      <div className="flex flex-row flex-wrap items-center gap-[16px]">
        <div className="flex-1 min-w-[260px] max-w-[440px]">
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label="Search posts"
            placeholder="Search by title or content..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <svg width="17" height="17" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="10" cy="10" r="7" stroke="#b5a03e" strokeWidth="2" />
                    <line x1="15" y1="15" x2="21" y2="21" stroke="#b5a03e" strokeWidth="2" strokeLinecap="round" />
                  </svg>
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className="w-[240px]">
          <Autocomplete
            options={FILTER_OPTIONS}
            value={categoryFilter}
            onChange={(_, newValue) => setCategoryFilter(newValue || "All")}
            getOptionLabel={(option) =>
              option === "All" ? "All Categories" : categoryLabel(option)
            }
            renderInput={(params) => (
              <TextField {...params} label="Filter by category" variant="outlined" />
            )}
            size="small"
            disableClearable
          />
        </div>
        <div className="w-[190px]">
          <Autocomplete
            options={DATE_FILTERS}
            value={dateFilter}
            onChange={(_, newValue) => setDateFilter(newValue || ANY_TIME)}
            renderInput={(params) => (
              <TextField {...params} label="Filter by date" variant="outlined" />
            )}
            size="small"
            disableClearable
          />
        </div>
      </div>

      {matchCount > 0 ? (
        <>
          {isFiltering && (
            <p className="text-[13px] text-dupontGray dark:text-darkTextSecondary">
              {matchCount} post{matchCount !== 1 ? "s" : ""}
              {isSearching && <> matching &ldquo;{search.trim()}&rdquo;</>}
              {categoryFilter !== "All" && (
                <> in {categoryLabel(categoryFilter)}</>
              )}
              {dateFilter !== ANY_TIME && <> &middot; {dateFilter.toLowerCase()}</>}
            </p>
          )}

          {/* Pinned posts sit above the categories so a collapsed section can
              never hide an announcement an admin deliberately pinned. */}
          {pinned.length > 0 && (
            <section className="flex flex-col gap-y-[12px]">
              <div className="flex flex-row items-center gap-x-[12px]">
                <span className="text-[13px] font-bold text-dupontBlue dark:text-dupontGold whitespace-nowrap">
                  &#128204; Pinned
                </span>
                <span className="h-[1px] flex-1 bg-dupontGold/30" />
              </div>
              <PostGrid posts={pinned} isAdmin={isAdmin} />
            </section>
          )}

          {sections.map((group) => (
            <CategorySection
              key={group.category}
              category={group.category}
              posts={group.posts}
              isAdmin={isAdmin}
              open={!collapsed[group.category]}
              onToggle={() =>
                setCollapsed((prev) => ({
                  ...prev,
                  [group.category]: !prev[group.category],
                }))
              }
            />
          ))}
        </>
      ) : (
        <div className="flex flex-col items-center py-[60px] gap-y-[16px]">
          <div className="text-[72px] opacity-80">&#9993;</div>
          <h3 className="text-[22px] font-display font-bold text-dupontBlue dark:text-darkText">
            {!isFiltering
              ? "No Posts Yet"
              : isSearching
              ? "No Matching Posts"
              : dateFilter !== ANY_TIME && categoryFilter === "All"
              ? `Nothing Posted ${dateFilter === "This month" ? "This Month" : dateFilter === "This year" ? "This Year" : "in the Last 3 Months"}`
              : `No Posts in "${categoryLabel(categoryFilter)}"`}
          </h3>
          <p className="text-[16px] font-body text-dupontGray dark:text-darkTextSecondary text-center leading-relaxed max-w-[420px]">
            {!isFiltering
              ? "Be the first to share something with the family!"
              : isSearching
              ? "Try a different word, or clear the search to see everything."
              : "Try widening the date range or choosing a different category."}
          </p>
        </div>
      )}
    </div>
  );
}

export default BulletinPosts;
